import { API_URL } from "@/config";
import axios, { AxiosError } from "axios";

const http = axios.create({ baseURL: API_URL, withCredentials: true });

function pickError(err: unknown): string {
    if (axios.isAxiosError(err)) {
        const d = (err as AxiosError<any>).response?.data;
        return d?.message ?? d?.error ?? err.message ?? "Request failed";
    }
    return err instanceof Error ? err.message : "Request failed";
}

export interface InsuranceCompanyUser {
    company_id: number;
    company_name: string;
    company_code: string;
    company_type: string;
    email: string;
    status: string;
    vehicle_types_covered: string[] | null;
    billing_invoice_no: number | null;
    lead_rate: string;
}

export async function loginInsurance(email: string, password: string) {
    try {
        const res = await fetch(`${API_URL}/insurance/auth/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password }),
            credentials: "include",
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message ?? `Login failed: ${res.status}`);
        return data;
    } catch (e) {
        throw new Error(pickError(e));
    }
}

export async function getCurrentInsurance(): Promise<InsuranceCompanyUser | null> {
    try {
        const res = await http.get<{ ok: boolean; company?: InsuranceCompanyUser }>(
            "/insurance/auth/me",
            { validateStatus: (s) => (s >= 200 && s < 300) || s === 401 }
        );
        return res.status === 401 ? null : res.data.company ?? null;
    } catch {
        return null;
    }
}

export async function logoutInsurance(): Promise<void> {
    try {
        await http.post("/insurance/auth/logout");
    } catch (e) {
        throw new Error(pickError(e));
    }
}

export async function getInsuranceQuoteRequests() {
    const res = await http.get("/insurance/quote-requests");
    return res.data;
}

export async function accessInsuranceLead(requestId: number) {
    const res = await http.post(`/insurance/quote-requests/${requestId}/access`);
    return res.data;
}

export async function submitInsuranceQuote(requestId: number, data: {
    deal_id?: number; customer_id?: number; coverage_type?: string;
    premium_monthly?: string; premium_annual?: string; deductible?: string;
    policy_number?: string; coverage_start?: string; coverage_end?: string; notes?: string;
}) {
    const res = await http.post(`/insurance/quote-requests/${requestId}/quote`, data);
    return res.data;
}

export async function uploadInsuranceBinder(quoteId: number, file: File, binder_number?: string) {
    const form = new FormData();
    form.append("binder", file);
    if (binder_number) form.append("binder_number", binder_number);
    const res = await http.patch(`/insurance/quotes/${quoteId}/binder`, form);
    return res.data;
}

export async function getInsuranceMySubmittedQuotes() {
    const res = await http.get("/insurance/my-submitted-quotes");
    return res.data;
}

export async function getInsuranceBilling() {
    const res = await http.get("/insurance/my-billing");
    return res.data;
}

// Customer calls
export async function createInsuranceQuoteRequest(data: {
    deal_id?: number; credit_app_id?: number; vehicle_vin?: string;
    vehicle_year?: number; vehicle_make?: string; vehicle_model?: string;
    vehicle_type?: string; notes?: string;
}) {
    const res = await http.post("/insurance/requests", data);
    return res.data;
}

export async function getMyInsuranceRequests() {
    const res = await http.get("/insurance/my-requests");
    return res.data;
}

export async function getMyInsuranceQuotesForDeal(dealId: number) {
    const res = await http.get(`/insurance/my-quotes/${dealId}`);
    return res.data;
}

export async function acceptInsuranceQuote(quoteId: number) {
    const res = await http.patch(`/insurance/quotes/${quoteId}/accept`);
    return res.data;
}

// Admin calls
export async function adminListInsuranceCompanies() {
    const res = await http.get("/admin/insurance/companies");
    return res.data;
}

export async function adminCreateInsuranceCompany(data: {
    company_name: string; company_code: string; email: string; password: string;
    phone?: string; contact_name?: string; license_states?: string; license_number?: string;
    company_type?: string; vehicle_types_covered?: string[]; is_major?: boolean;
    lead_rate?: string; logo_url?: string; website?: string; notes?: string;
}) {
    const res = await http.post("/admin/insurance/companies", data);
    return res.data;
}

export async function adminUpdateInsuranceCompanyStatus(id: number, status: string) {
    const res = await http.patch(`/admin/insurance/companies/${id}/status`, { status });
    return res.data;
}

export async function adminListLeadCharges(params?: { companyId?: number; month?: string; page?: number }) {
    const res = await http.get("/admin/insurance/lead-charges", { params });
    return res.data;
}

export async function adminListRecurringInvoices() {
    const res = await http.get("/admin/billing/recurring");
    return res.data;
}

export async function adminUpdateRecurringInvoice(id: number, data: {
    invoice_autocharge?: string; invoice_recurring_stopdate?: string; invoice_status?: string;
}) {
    const res = await http.patch(`/admin/billing/recurring/${id}`, data);
    return res.data;
}
